<template>
  <div class="donut-landing">
    <!-- SECTION 1: HERO -->
    <section class="hero-section">
      <div class="section-container hero-grid">
        <!-- Hero Left Info -->
        <div class="hero-info">
          <h1 class="hero-title">
            Онлайн запись и продажи без <br />
            <span class="accent-text">лишних переписок</span>
          </h1>

          <p class="hero-subtitle">
            {{ t("landing.hero.subtitle") }}
          </p>

          <div class="hero-cta-group">
            <BaseModalButton
              class="btn-primary btn-lg"
              :modal="MODAL.Audit"
            >
              Поможем настроить
            </BaseModalButton>
            <BaseButton
              class="btn-secondary btn-lg"
              @click="handleShowProblems"
            >
              Посмотреть примеры
            </BaseButton>
          </div>

          <!-- Hero Trust Strip -->
          <div class="hero-trust">
            <div class="avatar-stack">
              <img
                v-for="(src, i) in trustLogos"
                :key="i"
                :src="src"
                alt="Магазин"
                class="avatar-img"
                :style="{ marginLeft: i > 0 ? '-10px' : '0px' }"
              />
            </div>
            <div class="trust-text">
              <div class="stars-svg-row">
                <svg
                  v-for="star in 5"
                  :key="star"
                  class="star-svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </div>
              <p class="trust-subtitle">Пользователи доверяют MyDonut</p>
            </div>
          </div>
        </div>

        <!-- Hero Right: Main Banner with falling Telegram Notifications -->
        <div class="hero-visual beauty-nail-hero">
          <div class="bg-glow"></div>

          <!-- Banner Image Container -->
          <div class="banner-image-container">
            <BasePicture
              alt="New order"
              class="hero-banner-img"
              src="/images/banner_main.webp"
            />
          </div>

          <!-- Telegram Notification 1: Новая запись (order) -->
          <div class="tg-notif-card tg-notif-order">
            <div class="tg-notif-avatar-wrapper">
              <div class="tg-notif-avatar">
                <BasePicture
                  class="tg-notif-logo"
                  src="/logos/logo-black-new.svg"
                />
              </div>
              <div class="tg-notif-badge">
                <svg
                  class="tg-notif-plane"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M19.89 4.11a1.6 1.6 0 00-1.7-.22L3.62 10.15a1.61 1.61 0 00-.09 3l3.62 1.48a.79.79 0 00.82-.16l9-9a.4.4 0 01.57.57l-9 9a.79.79 0 00-.16.82l1.48 3.62a1.61 1.61 0 003-.09l6.26-14.57a1.6 1.6 0 00-.22-1.7z"
                  />
                </svg>
              </div>
            </div>
            <div class="tg-notif-content">
              <div class="tg-notif-header">
                <span class="tg-notif-title">Новая запись</span>
                <span class="tg-notif-time">now</span>
              </div>
              <div class="tg-notif-body">Анна · Маникюр · 14:00</div>
            </div>
          </div>

          <!-- Telegram Notification 2: Новый заказ (reminder) -->
          <div class="tg-notif-card tg-notif-reminder">
            <div class="tg-notif-avatar-wrapper">
              <div class="tg-notif-avatar">
                <BasePicture
                  class="tg-notif-logo"
                  src="/logos/logo-black-new.svg"
                />
              </div>
              <div class="tg-notif-badge">
                <svg
                  class="tg-notif-plane"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M19.89 4.11a1.6 1.6 0 00-1.7-.22L3.62 10.15a1.61 1.61 0 00-.09 3l3.62 1.48a.79.79 0 00.82-.16l9-9a.4.4 0 01.57.57l-9 9a.79.79 0 00-.16.82l1.48 3.62a1.61 1.61 0 003-.09l6.26-14.57a1.6 1.6 0 00-.22-1.7z"
                  />
                </svg>
              </div>
            </div>
            <div class="tg-notif-content">
              <div class="tg-notif-header">
                <span class="tg-notif-title">Новый заказ</span>
                <span class="tg-notif-time">now</span>
              </div>
              <div class="tg-notif-body">5 товаров · 120$ · Оплачен</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 2.5: WHY MYDONUT -->
    <section
      id="directions"
      class="why-section"
    >
      <div class="section-container">
        <div class="section-header align-center">
          <div class="section-badge">
            <svg
              class="badge-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              ></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
              ></line>
            </svg>
          </div>
          <h2 class="section-title">Зачем вам нужен MyDonut?</h2>
        </div>

        <div class="why-sphere-switcher-container">
          <div class="why-sphere-switcher">
            <button
              :class="['sphere-tab-btn', { active: currentSphere === 'booking' }]"
              @click="setSphere('booking')"
            >
              <svg
                class="sphere-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  ry="2"
                ></rect>
                <line
                  x1="16"
                  y1="2"
                  x2="16"
                  y2="6"
                ></line>
                <line
                  x1="8"
                  y1="2"
                  x2="8"
                  y2="6"
                ></line>
                <line
                  x1="3"
                  y1="10"
                  x2="21"
                  y2="10"
                ></line></svg
              >Онлайн-запись
            </button>
            <button
              :class="['sphere-tab-btn', { active: currentSphere === 'ecommerce' }]"
              @click="setSphere('ecommerce')"
            >
              <svg
                class="sphere-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line
                  x1="3"
                  y1="6"
                  x2="21"
                  y2="6"
                ></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path></svg
              >Продажа товаров
            </button>
          </div>
        </div>

        <div class="why-grid-layout">
          <!-- Desktop Reasons Nav (Vertical column) -->
          <div class="why-slider-nav-desktop">
            <div
              v-for="(slide, index) in whySlides"
              :key="index"
              :class="['why-nav-item', { active: activeSlide === index }]"
              @click="setActiveSlide(index)"
            >
              <div class="why-nav-header-row">
                <span class="why-nav-num">0{{ index + 1 }}</span>
                <h4 class="why-nav-title">{{ slide.shortTitle }}</h4>
              </div>
              <div
                class="why-nav-body"
                v-if="activeSlide === index"
              >
                <p class="why-nav-text">{{ slide.text }}</p>
              </div>
            </div>
          </div>

          <!-- Mobile: Horizontal scrollable tabs (wrapped) -->
          <div class="why-tabs-row">
            <button
              v-for="(slide, index) in whySlides"
              :key="index"
              :class="['why-tab-btn', { active: activeSlide === index }]"
              @click="setActiveSlide(index)"
            >
              <span class="why-tab-text">{{ slide.shortTitle }}</span>
            </button>
          </div>

          <!-- Single Screenshot placeholder with slide transition -->
          <div
            class="why-image-wrapper-container"
            @touchstart="onTouchStart"
            @touchend="onTouchEnd"
          >
            <div class="why-screenshot-wrapper">
              <Transition
                name="why-fade"
                mode="out-in"
              >
                <BasePicture
                  :key="`${currentSphere}-${activeSlide}`"
                  class="why-screenshot-img"
                  :src="whySlides[activeSlide].image || '/images/beauty-nail/work/demo.png'"
                />
              </Transition>
            </div>
          </div>

          <!-- Dynamic active reason description centered -->
          <div class="why-active-desc-container">
            <p class="why-active-desc-text">{{ whySlides[activeSlide].text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 2.6: OUR CLIENTS (Testimonials combined list-slider) -->
    <section
      id="testimonials"
      class="testimonials-section testimonials-combined-section"
    >
      <div class="section-container">
        <div
          class="section-header align-center"
          style="align-items: flex-start"
        >
          <div class="section-badge">
            <svg
              class="badge-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              />
            </svg>
          </div>

          <div style="display: flex; flex-direction: column">
            <h2 class="section-title">Наши клиенты</h2>
            <p class="section-subtitle">Переходите в социальные сети и смотрите закрепленные ссылки</p>
          </div>
        </div>

        <BaseSlider
          allow-touch-move
          class="testimonials__slider"
          :navigation="{
            nextEl: `#testimonials-slider-button-next`,
            prevEl: `#testimonials-slider-button-previous`,
          }"
          :slides="combinedTestimonials"
          slides-per-view="auto"
          :space-between="20"
          loop
        >
          <template #default="{ slide }">
            <div class="testimonial-card hover-lift shadow-sm">
              <div class="testimonial-header">
                <div class="testimonial-profile">
                  <div class="profile-img-container">
                    <img
                      :src="slide.photo"
                      :alt="slide.name"
                      class="profile-img"
                    />
                  </div>
                  <div class="profile-info">
                    <div class="profile-name">{{ slide.name }}</div>
                    <div class="profile-role">{{ slide.role }}</div>
                  </div>
                </div>
                <div class="testimonial-social-actions">
                  <a
                    v-if="slide.instagram"
                    :href="slide.instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-social btn-instagram"
                  >
                    <svg
                      class="social-logo-btn"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect
                        x="2"
                        y="2"
                        width="20"
                        height="20"
                        rx="5"
                        ry="5"
                      ></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line
                        x1="17.5"
                        y1="6.5"
                        x2="17.51"
                        y2="6.5"
                      ></line>
                    </svg>
                  </a>
                  <a
                    v-if="slide.telegram"
                    :href="slide.telegram"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-social btn-telegram"
                  >
                    <svg
                      class="social-logo-btn"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15.82-1.07 4.96-1.53 7.03-.2.87-.51 1.16-.83 1.19-.69.06-1.21-.46-1.88-.9-1.05-.68-1.64-1.11-2.66-1.78-1.18-.78-.41-1.21.26-1.9.17-.18 3.19-2.92 3.25-3.18.01-.03.01-.15-.06-.21-.07-.06-.18-.04-.25-.03-.1.02-1.7 1.08-4.81 3.18-.46.31-.87.47-1.24.46-.4-.01-1.17-.23-1.74-.42-.7-.23-1.26-.35-1.21-.74.03-.2.28-.41.77-.62 3.01-1.31 5.02-2.18 6.03-2.61 2.87-1.22 3.46-1.43 3.85-1.44.09 0 .28.02.4.12.1.08.13.2.14.29 0 .04-.01.18-.02.26z"
                      />
                    </svg>
                  </a>
                </div>
              </div>

              <div class="testimonial-divider"></div>

              <div class="stars-svg-row sm">
                <svg
                  v-for="star in 5"
                  :key="star"
                  class="star-svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
              </div>
              <div class="testimonial-heading">«{{ slide.title }}»</div>
              <p class="testimonial-quote">“{{ slide.quote }}”</p>
            </div>
          </template>
        </BaseSlider>

        <div class="testimonials__navigation">
          <BaseButton2
            v-for="direction in ['previous', 'next']"
            :id="`testimonials-slider-button-${direction}`"
            :key="direction"
            class="testimonials__button"
            :class="direction"
          >
            <BaseIcon2
              id="icon-cap"
              class="testimonials__icon"
            />
          </BaseButton2>
        </div>
      </div>
    </section>

    <!-- SECTION 3: PRICING CALCULATOR -->
    <section
      id="pricing"
      class="pricing-section"
    >
      <div class="section-container">
        <div class="section-header align-center">
          <div class="section-badge">
            <svg
              class="badge-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <rect
                x="2"
                y="4"
                width="20"
                height="16"
                rx="2"
                ry="2"
              />
              <line
                x1="12"
                y1="18"
                x2="12"
                y2="18"
              />
              <line
                x1="2"
                y1="10"
                x2="22"
                y2="10"
              />
            </svg>
          </div>
          <h2 class="section-title">Сколько стоит MyDonut?</h2>
        </div>

        <div class="pricing-card-wrapper">
          <div class="pricing-calculator card shadow-sm">
            <!-- Left inputs column -->
            <div class="calc-inputs-column">
              <!-- Tabs Services / Products -->
              <div class="calculator-tabs-row">
                <button
                  :class="['calc-tab-btn', { active: activeCalculatorTab === 'services' }]"
                  @click="activeCalculatorTab = 'services'"
                >
                  <svg
                    class="sphere-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line
                      x1="16"
                      y1="2"
                      x2="16"
                      y2="6"
                    ></line>
                    <line
                      x1="8"
                      y1="2"
                      x2="8"
                      y2="6"
                    ></line>
                    <line
                      x1="3"
                      y1="10"
                      x2="21"
                      y2="10"
                    ></line>
                  </svg>
                  Онлайн-запись
                </button>
                <button
                  :class="['calc-tab-btn', { active: activeCalculatorTab === 'products' }]"
                  @click="activeCalculatorTab = 'products'"
                >
                  <svg
                    class="sphere-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                    <line
                      x1="3"
                      y1="6"
                      x2="21"
                      y2="6"
                    ></line>
                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                  </svg>
                  Продажа товаров
                </button>
              </div>

              <div class="calc-parameter-header">
                <!-- Parameter 1: Masters (Only for services) -->
                <div
                  v-if="activeCalculatorTab === 'services'"
                  class="calc-parameter-row"
                >
                  <div class="param-details">
                    <span class="param-title">Мастера</span>
                    <span class="param-counter">{{ masters }}</span>
                  </div>
                  <div class="stepper-control">
                    <button
                      class="stepper-btn"
                      @click="masters = Math.max(1, masters - 1)"
                      :disabled="masters === 1"
                    >
                      −
                    </button>
                    <button
                      class="stepper-btn btn-plus"
                      @click="masters = Math.min(20, masters + 1)"
                      :disabled="masters === 20"
                    >
                      +
                    </button>
                  </div>
                </div>

                <!-- Parameter 2: Branches -->
                <div class="calc-parameter-row">
                  <div class="param-details">
                    <span class="param-title">Филиалы</span>
                    <span class="param-counter">{{ branches }}</span>
                  </div>
                  <div class="stepper-control">
                    <button
                      class="stepper-btn"
                      @click="branches = Math.max(1, branches - 1)"
                      :disabled="branches === 1"
                    >
                      −
                    </button>
                    <button
                      class="stepper-btn btn-plus"
                      @click="branches = Math.min(10, branches + 1)"
                      :disabled="branches === 10"
                    >
                      +
                    </button>
                  </div>
                </div>

                <!-- Parameter 3: Products -->
                <div class="calc-parameter-row">
                  <div class="param-details">
                    <span class="param-title">Товары</span>
                    <span class="param-counter">{{ productTiers[productTierIndex].limit }}</span>
                  </div>
                  <div class="stepper-control">
                    <button
                      class="stepper-btn"
                      @click="productTierIndex = Math.max(0, productTierIndex - 1)"
                      :disabled="productTierIndex === 0"
                    >
                      −
                    </button>
                    <button
                      class="stepper-btn btn-plus"
                      @click="productTierIndex = Math.min(productTiers.length - 1, productTierIndex + 1)"
                      :disabled="productTierIndex === productTiers.length - 1"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div class="calc-divider"></div>

              <!-- Billing Period toggles -->
              <div class="periods-scroll-row-mini">
                <button
                  v-for="period in periods"
                  :key="period.key"
                  :class="['period-rate-card-mini', { active: selectedPeriod === period.key }]"
                  @click="selectedPeriod = period.key"
                >
                  <span class="period-title-mini">{{ period.label }}</span>
                  <span
                    class="period-discount-badge-mini"
                    v-if="period.discount > 0"
                  >
                    -{{ period.discount }}%
                  </span>
                </button>
              </div>
            </div>

            <!-- Right totals column -->
            <div class="calc-totals-column">
              <!-- Promo Switcher -->
              <div class="promo-switch-box">
                <div class="promo-switch-label">
                  <span class="promo-switch-title">Скидка 15%</span>
                  <span class="promo-switch-desc">По промокоду START15 на первую оплату</span>
                </div>
                <label class="switch-control">
                  <input
                    type="checkbox"
                    v-model="isPromoApplied"
                  />
                  <span class="switch-slider"></span>
                </label>
              </div>

              <!-- Total price output -->
              <div class="total-price-wrapper">
                <div class="total-price-section">
                  <div class="total-header">Итого к оплате:</div>
                  <div class="price-display-wrapper">
                    <div class="total-big-price">${{ totalToPay }}</div>
                    <div class="total-monthly-hint">
                      <span v-if="getSelectedPeriodMonths() > 1">(${{ finalMonthly }}/мес)</span>
                      <span v-else>/мес</span>
                    </div>
                  </div>
                </div>
                <div class="pricing-roi-callout animate-pulse-slow">
                  <span class="roi-strong-text">Окупается всего одной продажей!</span>
                </div>
                <div
                  class="cta-buttons"
                  style="flex-direction: column; width: 100%"
                >
                  <BaseModalButton
                    class="btn-primary btn-lg"
                    :modal="MODAL.Audit"
                  >
                    Хочу помощь с настройкой
                  </BaseModalButton>
                  <BaseModalButton
                    class="btn-secondary btn-lg"
                    style="background-color: white"
                    :modal="MODAL.AuthRegistration"
                  >
                    Настрою сам
                  </BaseModalButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 6: CASES (Swiper Examples) -->
    <section
      class="testimonials-section section-cases"
      id="examples"
    >
      <div class="section-container">
        <div class="section-header align-left">
          <div class="section-badge">
            <svg
              class="badge-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="2"
                y="7"
                width="20"
                height="14"
                rx="2"
                ry="2"
              />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <div style="display: flex; flex-direction: column">
            <h2 class="section-title">Посмотрите демо</h2>
            <p class="section-subtitle">Откройте демо проекты и оформите онлайн-запись или заказ</p>
          </div>
        </div>

        <BaseSlider
          allow-touch-move
          class="section-cases__slider"
          :navigation="{
            nextEl: `#cases-slider-button-next`,
            prevEl: `#cases-slider-button-previous`,
          }"
          :slides="SECTION_CARDS"
          slides-per-view="auto"
          :space-between="20"
          loop
        >
          <template #default="{ slide, index }">
            <div class="case-slide testimonial-card hover-lift shadow-sm">
              <BasePicture
                class="case-slide__image"
                :src="`/images/demo/${slide.image}`"
              />

              <div class="case-slide__wrapper">
                <div class="case-slide__header">
                  <span class="case-slide__badge"> {{ t("landing.cases.badge") }} #{{ index + 1 }} </span>

                  <h3 class="case-slide__title">
                    {{ slide.title }}
                  </h3>
                </div>

                <p class="case-slide__text">
                  {{ slide.description }}
                </p>

                <BaseLink
                  class="btn-secondary btn-sm case-slide__link"
                  :href="slide.href"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <BaseIcon2
                    id="icon-telegram-full"
                    color="var(--orange-1)"
                    height="20"
                    sprite="socials"
                    width="20"
                    style="margin-right: 4px"
                  />
                  {{ t("landing.cases.button.view") }}
                </BaseLink>
              </div>
            </div>
          </template>
        </BaseSlider>

        <div class="section-cases__navigation">
          <BaseButton2
            v-for="direction in ['previous', 'next']"
            :id="`cases-slider-button-${direction}`"
            :key="direction"
            class="section-cases__button"
            :class="direction"
          >
            <BaseIcon2
              id="icon-cap"
              class="section-cases__icon"
            />
          </BaseButton2>
        </div>
      </div>
    </section>

    <!-- SECTION 9: COMPARISON -->
    <section
      id="comparison"
      class="comparison-section"
    >
      <div class="section-container">
        <div class="section-header align-center">
          <div class="section-badge">
            <svg
              class="badge-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="8"
                r="7"
              ></circle>
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
            </svg>
          </div>
          <h2 class="section-title">Чем MyDonut лучше других сервисов?</h2>
        </div>

        <div class="table-scroll-container">
          <table class="comparison-table">
            <thead>
              <tr>
                <th class="table-param-header">Параметр</th>
                <th class="table-donut-header">
                  <div class="donut-header-content">
                    <span class="donut-title">MyDonut</span>
                  </div>
                </th>
                <th class="table-competitor-header">
                  <div class="comp-title">Другие сервисы</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in compRows"
                :key="idx"
              >
                <td>
                  <div class="cell-param">
                    <!-- Render custom SVG icon per parameter instead of emoji -->
                    <div class="param-icon-wrapper">
                      <svg
                        v-if="idx === 0"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <rect
                          x="2"
                          y="3"
                          width="20"
                          height="14"
                          rx="2"
                          ry="2"
                        />
                        <line
                          x1="8"
                          y1="21"
                          x2="16"
                          y2="21"
                        />
                        <line
                          x1="12"
                          y1="17"
                          x2="12"
                          y2="21"
                        />
                      </svg>
                      <svg
                        v-else-if="idx === 1"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <rect
                          x="2"
                          y="4"
                          width="20"
                          height="16"
                          rx="2"
                          ry="2"
                        />
                        <line
                          x1="12"
                          y1="18"
                          x2="12"
                          y2="18"
                        />
                        <line
                          x1="2"
                          y1="10"
                          x2="22"
                          y2="10"
                        />
                      </svg>
                      <svg
                        v-else-if="idx === 2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <rect
                          x="5"
                          y="2"
                          width="14"
                          height="20"
                          rx="2"
                          ry="2"
                        />
                        <line
                          x1="12"
                          y1="18"
                          x2="12.01"
                          y2="18"
                        />
                      </svg>
                      <svg
                        v-else-if="idx === 3"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <svg
                        v-else-if="idx === 4"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                      <svg
                        v-else-if="idx === 5"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        />
                        <path
                          d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                        />
                      </svg>
                    </div>
                    <span class="param-label">{{ row.label }}</span>
                  </div>
                </td>
                <td class="cell-donut">
                  <div class="badge-status-container">
                    <span :class="['table-badge', row.donut.type]">
                      <span class="table-badge-icon">
                        <svg
                          viewBox="0 0 24 24"
                          width="12"
                          height="12"
                          stroke="currentColor"
                          stroke-width="3"
                          fill="none"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {{ row.donut.text }}
                    </span>
                  </div>
                </td>
                <td class="cell-competitor">
                  <div class="badge-status-container">
                    <span :class="['table-badge', row.rival.type]">
                      <span class="table-badge-icon">
                        <svg
                          v-if="row.rival.type === 'bad'"
                          viewBox="0 0 24 24"
                          width="12"
                          height="12"
                          stroke="currentColor"
                          stroke-width="3"
                          fill="none"
                        >
                          <line
                            x1="18"
                            y1="6"
                            x2="6"
                            y2="18"
                          ></line>
                          <line
                            x1="6"
                            y1="6"
                            x2="18"
                            y2="18"
                          ></line>
                        </svg>
                        <span
                          v-else
                          style="font-size: 20px; line-height: 14px"
                        >
                          *
                        </span>
                      </span>
                      {{ row.rival.text }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="comparison-footer">
          <p class="comparison-footnote">* Цена с учетом интеграций и полноценных функций</p>
        </div>
      </div>
    </section>

    <!-- SECTION 10: FINAL CTA -->
    <section class="final-cta-section">
      <div class="cta-bg-glow"></div>
      <div class="section-container cta-grid-layout">
        <!-- Left: Info -->
        <div class="cta-info-col">
          <h2 class="cta-headline">
            Проведём аудит, перенесём базу клиентов и <span class="accent-text">настроим всё за вас</span>
          </h2>
          <p class="cta-subtitle">Поможем перейти с других сервисов и настроим проект без потери клиентов.</p>
          <div class="cta-buttons">
            <BaseModalButton
              class="btn-primary btn-lg"
              :modal="MODAL.Audit"
            >
              Начать с нашей помощью
            </BaseModalButton>
            <BaseModalButton
              class="btn-secondary btn-lg"
              :modal="MODAL.AuthRegistration"
            >
              Начать самостоятельно
            </BaseModalButton>
          </div>
          <div class="cta-micro-trust">
            <div class="trust-item">
              <span class="check-circle-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="8"
                  height="8"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              14 дней бесплатно
            </div>
            <div class="trust-item">
              <span class="check-circle-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="8"
                  height="8"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              Отмена в любой момент
            </div>
          </div>
        </div>

        <!-- Right: Image Mockup -->
        <div class="cta-image-col">
          <BasePicture
            class="cta-mockup-img"
            src="/images/ecommerce/hero/application.webp"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const { t, locale } = useI18n();

const handleShowProblems = () => {
  const advantagesElement = document.querySelector("#examples");
  if (advantagesElement) {
    advantagesElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const SECTION_CARDS = computed(() => [
  {
    description: "Барбершоп",
    href: "https://t.me/DalyCityBot",
    image: "daly_logo.webp",
    reverse: false,
    title: "Daly City",
  },
  {
    description: "Спортивная одежда",
    href: "https://t.me/RunningFiveBot",
    image: "r5_logo.webp",
    reverse: true,
    title: "Running Five",
  },
  {
    description: "Салон красоты",
    href: "https://t.me/LunaBeautyStudioBot",
    image: "luna_logo.webp",
    reverse: true,
    title: "Luna Beauty Studio",
  },
  {
    description: "Украшения и акссесуары",
    href: "https://t.me/lunaraJewerlyBot",
    image: "lunara_logo.webp",
    reverse: true,
    title: "Lunara Jewelry",
  },
  {
    description: "Студия маникюра",
    href: "https://t.me/beauty_glow_nails_bot",
    image: "beauty_logo.webp",
    reverse: true,
    title: "Beauty & Glow Nails",
  },
]);

const currentSphere = ref<"booking" | "ecommerce">("booking");
const activeSlide = ref(0);

const bookingWhySlides = [
  {
    shortTitle: "Уменьшить переписки в чатах 💬",
    text: "Клиент сам записывается, отменяет или переносит записи",
    image: "/images/beauty-nail/work/pain-1.png",
  },
  {
    shortTitle: "Перестать напоминать вручную ✍️️️",
    text: "Система сама напомнит или подтвердит запись клиента",
    image: "/images/beauty-nail/work/pain-2.png",
  },
  {
    shortTitle: "Не переплачивать за CRM 💸",
    text: "Всё включено в тариф и управляется с телефона — без сложных настроек и интеграций",
    image: "/images/beauty-nail/work/pain-3.png",
  },
  {
    shortTitle: "Заполнить пустые окна 🗓️",
    text: "Клиенты подписываются на уведомления о пустых окнах и записываются сами",
    image: "/images/beauty-nail/work/pain-4.png",
  },
  {
    shortTitle: "Увеличить повторные визиты 📈",
    text: "Клиенты получают бонусы за посещения и напоминания о повторном визите",
    image: "/images/beauty-nail/work/pain-5.png",
  },
];

const ecommerceWhySlides = [
  {
    shortTitle: "Уменьшить переписки в чате 💬",
    text: "Клиент смотрит каталог, видит наличие и делает заказ",
    image: "/images/ecommerce/work/pain-1.webp",
  },
  {
    shortTitle: "Увеличить повторные заказы ✍️️️",
    text: "Клиенты получают бонусы за заказы и рассылки об акциях",
    image: "/images/ecommerce/work/pain-2.webp",
  },
  {
    shortTitle: "Настроить продажи в Telegram 🛍️",
    text: "Продажи там, где сидит клиент без лишних переходов на сайт",
    image: "/images/ecommerce/work/pain-3.webp",
  },
  {
    shortTitle: "Не терять заказы в чатах 📌",
    text: "Единый чат с клиентом - все контакты сохраняются в вашей базе",
    image: "/images/ecommerce/work/pain-4.webp",
  },
  {
    shortTitle: "Не переплачивать за CRM 💸",
    text: "Всё включено в тариф и управляется с телефона — без сложных настроек и интеграций",
    image: "/images/ecommerce/work/pain-5.webp",
  },
  {
    shortTitle: "Не терять историю клиентов 👤",
    text: "Вся история общения, контакты и заказы клиента хранятся в одной карточке.",
    image: "/images/ecommerce/work/pain-6.webp",
  },
];

const whySlides = computed(() => {
  return currentSphere.value === "booking" ? bookingWhySlides : ecommerceWhySlides;
});

let slideInterval: any = null;

const startSlideTimer = () => {
  stopSlideTimer();
  slideInterval = setInterval(() => {
    activeSlide.value = (activeSlide.value + 1) % whySlides.value.length;
  }, 7000);
};

const stopSlideTimer = () => {
  if (slideInterval) {
    clearInterval(slideInterval);
    slideInterval = null;
  }
};

const setSphere = (sphere: "booking" | "ecommerce") => {
  currentSphere.value = sphere;
  activeSlide.value = 0;
  startSlideTimer();
};

const setActiveSlide = (index: number) => {
  activeSlide.value = index;
  startSlideTimer();
};

const touchStartX = ref(0);
const onTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX;
};
const onTouchEnd = (e: TouchEvent) => {
  const touchEndX = e.changedTouches[0].clientX;
  const diffX = touchEndX - touchStartX.value;
  if (Math.abs(diffX) > 50) {
    if (diffX < 0) {
      setActiveSlide((activeSlide.value + 1) % whySlides.value.length);
    } else {
      setActiveSlide((activeSlide.value - 1 + whySlides.value.length) % whySlides.value.length);
    }
  }
};

const combinedTestimonials = [
  {
    photo: "/images/beauty-nail/demo/renata.jpeg",
    name: "Рената",
    role: "Мастер маникюра",
    instagram: "https://www.instagram.com/renagaripova_nails/",
    telegram: "https://t.me/renagaripova_bot",
    title: "Экономия и порядок с телефона",
    quote: "В Altegio платила $35 только за уведомления. Здесь это уже включено с мобильным доступом",
  },
  {
    photo: "/images/ecommerce/demo/onejar.jpg",
    name: "OneJarPlease",
    role: "Вкусная домашняя еда в банках",
    instagram: "https://www.instagram.com/onejarplease",
    telegram: "https://t.me/onejarplsbot",
    title: "Заказы без долгих переписок",
    quote:
      "Раньше с клиентами уходили часы на переписку — адрес, оплата, детали заказа. Теперь всё это делает сам бот с приложением",
  },
  {
    photo: "/images/beauty-nail/demo/kirill.jpg",
    name: "Кирилл",
    role: "Массажист",
    instagram: "https://www.instagram.com/massage.therapist.kirill/",
    telegram: "https://t.me/MassageKirill_bot",
    title: "Ноль ручной работы",
    quote: "В Dikidi вручную продлевал график, записи постоянных клиентов и заполнял окна. Сейчас все автоматически",
  },
  {
    photo: "/images/ecommerce/demo/joonies.jpg",
    name: "Joonies",
    role: "Подгузники, пелёнки, салфетки и товары для мам",
    instagram: "https://www.instagram.com/joonies_georgia/",
    telegram: "https://t.me/jooniesGeorgiaBot",
    title: "Дешевле и легкий переезд",
    quote:
      "Прошлый сервис дорогой и совсем не гибкий. А главное — нам помогли перенести всё, сами бы откладывали это месяцами",
  },
  {
    photo: "/images/beauty-nail/demo/salon.jpg",
    name: "Aesthetic studio",
    role: "Массажный салон",
    instagram: "https://www.instagram.com/aesthetic.studio.georgia/",
    telegram: "https://t.me/aesthetic_studio_bot",
    title: "Простота без доплат",
    quote: "В Altegio всё за доп. плату и сложно настроить. Тут проще — и поддержка всегда на связи",
  },
  {
    photo: "/images/ecommerce/demo/vesnail.jpg",
    name: "Vesnail Georgia",
    role: "Товары для маникюра",
    instagram: "https://www.instagram.com/_vesnail_georgia.official/",
    telegram: "https://t.me/vesnail_georgia_bot",
    title: "Продажи и клиенты в одном месте",
    quote: "Раньше продажи были разбросаны по разным площадкам. Здесь всё собрано — и управляем прямо с телефона",
  },
];

const route = useRoute();
const { openModal } = useModalStore();

onMounted(() => {
  startSlideTimer();

  const modalQuery = route.query.modal as string | undefined;

  if (modalQuery) {
    const normalized = modalQuery.toLowerCase();
    if (normalized === "help" || normalized === "audit" || normalized === "modal-audit") {
      openModal(MODAL.Audit);
    } else if (
      normalized === "registration" ||
      normalized === "register" ||
      normalized === "reg" ||
      normalized === "modal-auth-registration"
    ) {
      openModal(MODAL.AuthRegistration);
    } else {
      openModal(modalQuery);
    }
  } else if (route.query.help !== undefined) {
    openModal(MODAL.Audit);
  } else if (
    route.query.registration !== undefined ||
    route.query.register !== undefined ||
    route.query.reg !== undefined
  ) {
    openModal(MODAL.AuthRegistration);
  }
});

onUnmounted(() => {
  stopSlideTimer();
});

const isAuthOrHelpModal = computed(() => {
  const modal = route.query.modal as string | undefined;
  if (!modal) {
    return (
      route.query.help !== undefined ||
      route.query.register !== undefined ||
      route.query.registration !== undefined ||
      route.query.reg !== undefined
    );
  }
  const normalized = modal.toLowerCase();
  return (
    normalized === "modal-auth-login" ||
    normalized === "modal-auth-registration" ||
    normalized === "registration" ||
    normalized === "register" ||
    normalized === "reg" ||
    normalized === "help" ||
    normalized === "audit" ||
    normalized === "modal-audit"
  );
});

if (isAuthOrHelpModal.value) {
  useHead({
    meta: [{ name: "robots", content: "noindex,nofollow" }],
  });
}

definePageMeta({
  layout: "landings",
  middleware: [
    async (to) => {
      const { tokenState } = useAuthStore();
      const { $env, $i18n } = useNuxtApp();

      const hasModal =
        to.query.modal ||
        to.query.help !== undefined ||
        to.query.register !== undefined ||
        to.query.registration !== undefined ||
        to.query.reg !== undefined;

      if ($env.isServer && tokenState.value.accessToken && !hasModal) {
        return navigateTo("/" + $i18n.locale.value + "/client/projects");
      }

      if ($env.isClient && hasModal) {
        const { removeRefreshToken } = useAuthToken();

        removeRefreshToken();
      }
    },
  ],
});

useHead({
  meta: [
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no",
    },
  ],
});

useSeoMeta({
  title: "Автоматизация продаж и записи в мессенджерах без лишних переписок — MyDonut",
  description:
    "Клиенты сами записываются и покупают товары в Telegram и по ссылке в Instagram. Делаем аудит и помогаем перенести базу клиентов.",
});

const trustLogos = [
  "/images/ecommerce/demo/joonies.jpg",
  "/images/beauty-nail/demo/kirill.jpg",
  "/images/ecommerce/demo/vesnail.jpg",
  "/images/beauty-nail/demo/renata.jpeg",
  "/images/ecommerce/demo/onejar.jpg",
];

// PRICING CALCULATOR STATE
const activeCalculatorTab = ref("services"); // 'services' | 'products'
const masters = ref(1);
const branches = ref(1);
const productTierIndex = ref(0);
const isPromoApplied = ref(true);

watch(activeCalculatorTab, (newTab) => {
  if (newTab === "services") {
    masters.value = 1;
    branches.value = 1;
    productTierIndex.value = 0;
  } else if (newTab === "products") {
    masters.value = 1;
    branches.value = 1;
    productTierIndex.value = 1;
  }
});

const basePrice = 8;
const branchPrice = 3;
const employeePrice = 4;
const productTiers = [
  { limit: "0", price: 0 },
  { limit: "30", price: 4 },
  { limit: "60", price: 6 },
  { limit: "100", price: 8 },
  { limit: "200", price: 15 },
  { limit: "500", price: 25 },
  { limit: "1000", price: 40 },
];

const periods = [
  { key: "monthly", label: "1 мес", months: 1, discount: 0 },
  { key: "three_months", label: "3 мес", months: 3, discount: 5 },
  { key: "six_months", label: "6 мес", months: 6, discount: 10 },
  { key: "yearly", label: "1 год", months: 12, discount: 20 },
];

const selectedPeriod = ref("monthly");
const getSelectedPeriodMonths = () => {
  const p = periods.find((p) => p.key === selectedPeriod.value);
  return p ? p.months : 1;
};

const getCalculatedMonthly = (discountPercent: number) => {
  const activeEmployeePrice = activeCalculatorTab.value === "services" ? masters.value * employeePrice : 0;
  const baseVal =
    basePrice + branches.value * branchPrice + activeEmployeePrice + productTiers[productTierIndex.value].price;
  const periodPrice = baseVal * (1 - discountPercent);
  const promoPrice = periodPrice * (1 - (isPromoApplied.value ? 0.15 : 0));
  return promoPrice.toFixed(2);
};

const finalMonthly = computed(() => {
  const p = periods.find((p) => p.key === selectedPeriod.value) || periods[0];
  return getCalculatedMonthly(p.discount / 100);
});

const totalToPay = computed(() => {
  const p = periods.find((p) => p.key === selectedPeriod.value) || periods[0];
  return (finalMonthly.value * p.months).toFixed(2);
});

// COMPARISON DATA
const compRows = [
  {
    icon: "📦",
    label: "Функционал",
    donut: { type: "good", text: "все включено" },
    rival: { type: "bad", text: "нужны интеграции" },
  },
  {
    icon: "💰",
    label: "Стоимость",
    donut: { type: "good", text: "от $12" },
    rival: { type: "warning", text: "от $14-20" },
  },
  {
    icon: "📱",
    label: "Управление",
    donut: { type: "good", text: "с телефона" },
    rival: { type: "bad", text: "иногда нужен ноутбук" },
  },
  {
    icon: "🛟",
    label: "Поддержка",
    donut: { type: "good", text: "всегда на связи" },
    rival: { type: "bad", text: "бот и ожидание" },
  },
  {
    icon: "💬️",
    label: "Чат с клиентом",
    donut: { type: "good", text: "через бота" },
    rival: { type: "bad", text: "ограничено или отсутствует" },
  },
  {
    icon: "⚙️",
    label: "Настройка",
    donut: { type: "good", text: "под ключ" },
    rival: { type: "bad", text: "настраиваете по видео" },
  },
];
</script>

<style scoped>
/* GENERAL BRAND DESIGN TOKENS */
.donut-landing {
  min-height: 100vh;
  font-family:
    "Plus Jakarta Sans",
    "Inter",
    system-ui,
    -apple-system,
    sans-serif;
  color: #1f2937;
  overflow-x: hidden;
  touch-action: manipulation;
  background-color: var(--grey-7);
}

/* SECTION CONTAINERS */
.section-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* BUTTONS */
.btn-primary {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  background-color: #f25c45;
  box-shadow: 0 4px 14px rgb(242 92 69 / 20%);
  transition: all 0.2s;
  cursor: pointer;
  text-decoration: none;
}

.btn-primary:hover {
  transform: translateY(-1.5px);
  box-shadow: 0 6px 18px rgb(242 92 69 / 30%);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-secondary {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  color: #1f2937;
  text-decoration: none;
  border: 1px solid #eaecef;
  border-radius: 12px;
  background-color: #f8f9fb;
  transition: all 0.2s;
  cursor: pointer;
}

.btn-secondary:hover {
  transform: translateY(-1.5px);
  color: #f25c45;
  border-color: #f25c45;
  background-color: #ffffff;
}

.btn-sm {
  padding: 8px 14px;
  font-size: 12.5px;
  border-radius: 10px;
}

.btn-md {
  padding: 10px 20px;
  font-size: 13.5px;
}

.btn-lg {
  padding: 14px 22px;
  font-size: 14px;
}

/* SECTION HEADER STYLES */
.section-header {
  display: flex;
  gap: 10px;
  margin-bottom: 32px;
}

.section-header.align-center {
  align-items: center;
  justify-content: flex-start;
}

.section-header.align-left {
  align-items: flex-start;
  justify-content: flex-start;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-weight: 700;
  font-size: 12px;
  color: #f25c45;
  border: 1px solid #ffd5cf;
  border-radius: 20px;
  background-color: #fff4f2;
}

.badge-icon-svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.section-title {
  margin: 0;
  font-weight: 850;
  font-size: 26px;
  color: #1f2937;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.section-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.45;
  width: 100%;
}

.section-subtitle.align-center {
  text-align: center;
}

/* SECTION 1: HERO */
.hero-section {
  padding: 30px 0 30px;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 48px;
}

.hero__badge-wrapper {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  font-weight: 700;
  font-size: 12px;
  color: #f25c45;
  border: 1px solid #ffd5cf;
  border-radius: 20px;
  background-color: #fff4f2;
}

.hero-title {
  margin: 0 0 16px;
  font-weight: 850;
  font-size: 32px;
  color: #1f2937;
  line-height: 1.15;
  letter-spacing: -0.03em;
  min-height: 80px;
}

.typing-text {
  display: inline-block;
}

.typing-cursor {
  display: inline-block;
  color: #f25c45;
  animation: blink 1s infinite;
  margin-left: 2px;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

.accent-text {
  color: #f25c45;
}

.hero-subtitle {
  margin: 0 0 24px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.hero-cta-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 28px;
}

.hero-trust {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-stack {
  display: flex;
}

.avatar-img {
  width: 32px;
  height: 32px;
  border: 2.5px solid #ffffff;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 5px rgb(0 0 0 / 6%);
}

.trust-text {
  display: flex;
  flex-direction: column;
}

.stars-svg-row {
  display: flex;
  gap: 2px;
}

.star-svg {
  width: 14px;
  height: 14px;
  color: #f59e0b;
}

.trust-subtitle {
  margin: 1px 0 0;
  font-size: 11.5px;
  color: #6b7280;
}

/* HERO VISUALS & PHONES */
.hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 350px;
}

.bg-glow {
  position: absolute;
  z-index: 0;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgb(242 92 69 / 8%) 0%, transparent 70%);
  pointer-events: none;
}

.banner-image-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.hero-banner-img {
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 15px 35px rgb(0 0 0 / 8%));
}

.tg-notif-card {
  position: absolute;
  z-index: 1;
  transform-origin: top center;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 280px;
  max-width: 85vw;
  padding: 10px 14px;
  opacity: 0;
  border: 1px solid rgb(255 255 255 / 8%);
  border-radius: 18px;
  background: rgb(43 43 46 / 85%);
  backdrop-filter: blur(20px) saturate(190%);
  box-shadow:
    0 10px 30px rgb(0 0 0 / 25%),
    inset 0 0.5px 0 rgb(255 255 255 / 15%);
  pointer-events: none;
}

.tg-notif-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
}

.tg-notif-avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 10px;
  background: #ffffff;
}

.tg-notif-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.tg-notif-badge {
  position: absolute;
  right: -3px;
  bottom: -3px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  border: 1.5px solid rgb(43 43 46 / 85%);
  border-radius: 50%;
  background: #2aabee;
}

.tg-notif-plane {
  width: 8px;
  height: 8px;
  color: #ffffff;
}

.tg-notif-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tg-notif-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.tg-notif-title {
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.tg-notif-time {
  font-weight: 400;
  font-size: 11px;
  color: rgb(255 255 255 / 45%);
  white-space: nowrap;
}

.tg-notif-body {
  font-weight: 400;
  font-size: 12.5px;
  color: rgb(255 255 255 / 90%);
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.tg-notif-order {
  top: 0;
  left: 10px;
  animation: tgNotifFall 0.7s cubic-bezier(0.2, 0.9, 0.3, 1.3) 0.8s forwards;
}

.tg-notif-reminder {
  right: -20px;
  bottom: -15px;
  animation: tgNotifFall 0.7s cubic-bezier(0.2, 0.9, 0.3, 1.3) 1.8s forwards;
}

@keyframes tgNotifFall {
  0% {
    transform: translateY(-60px);
    opacity: 0;
  }
  60% {
    transform: translateY(8px);
    opacity: 1;
  }
  80% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* SECTION 2: NAVIGATION CARDS */
.section-navigation {
  padding: 50px 0;
}

.nav-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.nav-card {
  background: #ffffff;
  padding: 20px;
  border-radius: 32px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.nav-card--beauty {
  background: linear-gradient(135deg, #ffffff 0%, #fff5f3 100%);
  border: 1px solid #ffdcd6;
}

.nav-card--ecommerce {
  background: linear-gradient(135deg, #ffffff 0%, #f4f6ff 100%);
  border: 1px solid #d2daff;
}

.nav-card::after {
  content: "";
  position: absolute;
  top: -50px;
  right: -50px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.25;
  z-index: 1;
  transition: all 0.4s ease;
  pointer-events: none;
}

.nav-card--beauty::after {
  background: #f25c45;
}

.nav-card--ecommerce::after {
  background: #4f46e5;
}

.nav-card:hover::after {
  transform: scale(1.6);
  opacity: 0.4;
}

.nav-card-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 16px;
  transition: all 0.3s ease;
  z-index: 2;
}

@media (mix-width: 992px) {
  .nav-card-icon-wrapper {
    width: 52px;
    height: 52px;
  }
}

.nav-card--beauty .nav-card-icon-wrapper {
  background-color: #fff0ed;
  color: #f25c45;
  border: 1px solid #ffd5cf;
}

.nav-card--ecommerce .nav-card-icon-wrapper {
  background-color: #e8ebff;
  color: #4f46e5;
  border: 1px solid #c7d2fe;
}

.nav-card-icon {
  width: 24px;
  height: 24px;
}

.nav-card:hover .nav-card-icon-wrapper {
  transform: scale(1.1) rotate(3deg);
}

@media (min-width: 992px) {
  .nav-card {
    padding: 32px;
  }
}

.nav-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
}

.nav-card--beauty:hover {
  border-color: #f25c45;
  box-shadow: 0 12px 40px rgba(242, 92, 69, 0.12);
}

.nav-card--ecommerce:hover {
  border-color: #4f46e5;
  box-shadow: 0 12px 40px rgba(79, 70, 229, 0.12);
}

.nav-card-content {
  flex: 1;
  position: relative;
  width: 100%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.nav-card-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--grey-1);
  margin: 0 0 4px 0;
}

.nav-card-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.nav-card-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14.5px;
  color: var(--grey-1-5);
  line-height: 1.4;
  padding-left: 0;
}

.nav-card-list li::before {
  display: none !important;
}

.nav-card-check {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.nav-card--beauty .nav-card-check {
  color: #f25c45;
}

.nav-card--ecommerce .nav-card-check {
  color: #4f46e5;
}

.nav-card-action {
  margin-top: auto;
  width: 100%;
}

.nav-card-action .btn-primary {
  width: 100%;
  text-align: center;
  justify-content: center;
}

.nav-card--beauty .btn-primary {
  background-color: #f25c45 !important;
  border-color: #f25c45 !important;
}

.nav-card--beauty .btn-primary:hover {
  background-color: #e04b33 !important;
  border-color: #e04b33 !important;
}

.nav-card--ecommerce .btn-primary {
  background-color: #4f46e5 !important;
  border-color: #4f46e5 !important;
}

.nav-card--ecommerce .btn-primary:hover {
  background-color: #3b31d2 !important;
  border-color: #3b31d2 !important;
}

/* SECTION 3: WHY DONUT */
.why-section {
  padding: 50px 0 60px;
  border-top: 1px solid #eaecef;
  background-color: #ffffff;
}

.why-grid-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.why-slider-nav-desktop {
  display: none;
}

.why-tabs-row {
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  width: 100%;
  padding: 4px 4px 12px;
  white-space: break-spaces;
  overflow-x: auto;
}

.why-tabs-row::-webkit-scrollbar {
  display: none;
}

.why-tab-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  padding: 8px 14px;
  border: 1px solid #eaecef;
  border-radius: 12px;
  background: #f8f9fb;
  transition:
    border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  min-width: 110px;
  width: 100%;
}

.why-tab-btn.active {
  border-color: #f25c45;
  background: #fff4f2;
  box-shadow: 0 4px 12px rgb(242 92 69 / 8%);
}

.why-tab-text {
  font-weight: 600;
  font-size: 12px;
  color: #6b7280;
}

.why-tab-btn.active .why-tab-text {
  color: #f25c45;
}

.why-image-wrapper-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  touch-action: pan-y;
}

.why-screenshot-wrapper {
  position: relative;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  margin: 0 auto;
  aspect-ratio: 958 / 978;
}

.why-screenshot-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.why-fade-enter-active,
.why-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.why-fade-enter-from {
  transform: translateY(10px);
  opacity: 0;
}

.why-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.why-active-desc-container {
  width: 100%;
  max-width: 600px;
  margin: -8px auto 8px;
  text-align: center;
}

.why-active-desc-text {
  margin: 0;
  font-weight: 500;
  font-size: 16px;
  color: #4b5563;
  line-height: 1.5;
}

/* SPHERE SWITCHER */
.why-sphere-switcher-container {
  display: flex;
  justify-content: center;
  width: 100%;
}
.why-sphere-switcher {
  display: inline-flex;
  background: #f1f5f9;
  padding: 6px;
  border-radius: 99px;
  margin: 0 auto 32px auto;
  border: 1px solid #e2e8f0;
}
.sphere-tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 99px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.sphere-tab-btn:hover {
  color: #1e293b;
}
.sphere-tab-btn.active {
  background: #ffffff;
  color: #f25c45;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.sphere-icon {
  width: 16px;
  height: 16px;
}

/* SECTION 2.6: COMBINED TESTIMONIALS */
.testimonials-combined-section {
  padding: 50px 0 60px;
  border-top: 1px solid #eaecef;
  background-color: #f8f9fb;
}

.testimonials__slider {
  margin: 0 -20px;
  padding: 0 20px;
}

.testimonials__slider :deep(.swiper-slide) {
  width: 280px;
  height: auto;
}

.testimonial-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px solid #eaecef;
  border-radius: 20px;
  background: #ffffff;
  height: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  transition: transform 0.3s ease;
}

.testimonial-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.profile-img-container {
  position: relative;
  flex-shrink: 0;
}

.profile-img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  background: #f3f4f6;
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-name {
  margin-bottom: 1px;
  font-weight: 750;
  font-size: 13.5px;
  color: #1f2937;
}

.profile-role {
  margin-bottom: 3px;
  font-size: 11.5px;
  color: #6b7280;
}

.testimonial-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.testimonial-social-actions {
  display: flex;
  gap: 8px;
}

.btn-social {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
}

.social-logo-btn {
  width: 15px;
  height: 15px;
}

.btn-instagram {
  color: #ffffff;
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
}

.btn-instagram:hover {
  transform: translateY(-1.5px);
  opacity: 0.95;
}

.btn-telegram {
  color: #ffffff;
  background-color: #2aabee;
}

.btn-telegram:hover {
  transform: translateY(-1.5px);
  opacity: 0.95;
}

.testimonial-divider {
  height: 1px;
  margin-bottom: 12px;
  background-color: #eaecef;
}

.testimonial-heading {
  margin-top: 8px;
  margin-bottom: 6px;
  font-weight: 750;
  font-size: 14.5px;
  color: #1f2937;
  line-height: 1.3;
}

.testimonial-quote {
  margin: 0;
  font-size: 13px;
  font-style: italic;
  color: #6b7280;
  line-height: 1.5;
}

.testimonials__navigation {
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  margin-top: 16px;
}

.testimonials__button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
}

.testimonials__button:hover {
  background-color: #f25c45;
  color: var(--white);
  border-color: #f25c45;
}

.testimonials__button.previous .testimonials__icon {
  transform: rotate(180deg);
}

.testimonials__icon {
  width: 16px;
  height: 16px;
}

/* SECTION 4: ADVANTAGES */
.section-advantages {
  padding: 50px 0;
}

.index__advantages {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  padding: 40px 24px;
  border-radius: 32px;
  background-color: var(--white);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
}

.index__advantages-item {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
}

.advantages__item-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  min-width: 45px;
  border-radius: 50%;
  background-color: var(--orange-3);
}

@media (min-width: 992px) {
  .advantages__item-icon {
    width: 56px;
    height: 56px;
    min-width: 56px;
  }
}

.advantages__item-title {
  margin: 0;
  font-weight: 800;
  font-size: 16px;
  color: #1f2937;
}

@media (min-width: 992px) {
  .advantages__item-title {
    font-size: 20px;
  }
}

.advantages__item-description {
  margin: 0;
  color: var(--grey-1-5);
  line-height: 1.5;
}

/* SECTION 5: VIEW */
.view {
  padding: 50px 0;
}

.view__text {
  text-align: center;
  margin-bottom: 32px;
}

.view__picture {
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  margin-inline: -16px;
  padding-top: 300px;
  padding-inline: 16px;
  overflow: hidden;
}

.view__image {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 450px;
  max-width: none;
}

.view__list {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 2;
}

.view__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 30px;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.view__icon {
  display: none;
  width: 40px;
  height: 40px;
}

.view__name {
  color: var(--grey-1-5);
  font-size: 14px;
}

.view__block-button {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

/* SECTION 6: CASES SLIDER */
.testimonials-section {
  padding: 50px 0 40px;
  border-top: 1px solid #eaecef;
  background-color: #f8f9fb;
}

.section-cases__navigation {
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  margin-top: 16px;
}

.section-cases__button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
}

.section-cases__button:hover {
  background-color: #f25c45;
  color: var(--white);
  border-color: #f25c45;
}

.section-cases__button.previous .section-cases__icon {
  transform: rotate(180deg);
}

.section-cases__icon {
  width: 16px;
  height: 16px;
}

.section-cases__slider {
  margin: 0 -20px;
  padding: 0 20px;
}

.section-cases__slider :deep(.swiper-slide) {
  width: 240px;
  height: auto;
}

.case-slide {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  height: 100%;
  border: 1px solid #eaecef;
}

.case-slide__image {
  width: 100%;
  height: 140px;
  background-color: var(--grey-7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eaecef;
  border-radius: 20px;
}

.case-slide__image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.case-slide__wrapper {
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  gap: 12px;
  flex: 1;
}

.case-slide__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.case-slide__badge {
  width: max-content;
  padding: 4px 10px;
  border-radius: 8px;
  background-color: #fff4f2;
  color: #f25c45;
  font-size: 11px;
  font-weight: 700;
}

.case-slide__title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  color: var(--grey-1);
  margin: 0;
}

.case-slide__text {
  color: var(--grey-1-5);
  font-size: 13.5px;
  line-height: 1.5;
  flex: 1;
  margin: 0;
}

.case-slide__link {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  margin-top: auto;
}

/* SECTION 7: WORK */
.work {
  padding: 50px 0;
}

.work__heading_block {
  text-align: center;
  margin-bottom: 32px;
}

.work__list {
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 16px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.work__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  row-gap: 12px;
  width: 100%;
  max-width: 320px;
  padding: 24px;
  border-radius: 24px;
  background-color: var(--white);
  border: 1px solid #eaecef;
}

.work__icon-img {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.work__item-title {
  margin: 0;
  font-weight: 750;
  color: #1f2937;
}

.work__item:last-child {
  background-color: var(--white);
  border-color: #f25c45;
}

.work__item-desc {
  margin: 0;
  color: var(--grey-1-5);
  line-height: 1.45;
}

.work__action-btn {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}

/* SECTION 8: PRICING */
.pricing-section {
  padding: 50px 0 60px;
  border-top: 1px solid #eaecef;
  background-color: #ffffff;
}

.pricing-calculator {
  display: flex;
  flex-direction: column;
  padding: 24px 12px;
  border: 1px solid #eaecef;
  border-radius: 24px;
  background-color: #f8f9fb;
}

.calculator-tabs-row {
  display: flex;
  background: #f1f5f9;
  padding: 6px;
  border-radius: 99px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
}

.calc-tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 99px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

@media (width >= 768px) {
  .calc-tab-btn {
    gap: 8px;
    padding: 10px 20px;
    font-size: 14px;
  }
}

.calc-tab-btn:hover {
  color: #1e293b;
}

.calc-tab-btn.active {
  background: #ffffff;
  color: #f25c45;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.calc-parameter-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.calc-parameter-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eaecef;
}

.param-details {
  display: flex;
  align-items: center;
  gap: 12px;
}

.param-title {
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
}

.param-counter {
  font-weight: 850;
  font-size: 20px;
  color: #f25c45;
}

.stepper-control {
  display: flex;
  align-items: center;
  border: 1px solid #eaecef;
  border-radius: 10px;
  overflow: hidden;
}

.stepper-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: #f8f9fb;
  font-weight: 700;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.stepper-btn.btn-plus {
  color: #f25c45;
}

.product-grades-container {
  margin-top: 24px;
  padding: 0 8px;
}

.grades-timeline-wrapper {
  position: relative;
  height: 40px;
}

.grades-timeline-track {
  position: absolute;
  top: 4px;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #e2e8f0;
  border-radius: 2px;
}

.grades-timeline-progress {
  height: 100%;
  background-color: #f25c45;
  border-radius: 2px;
  transition: width 0.2s ease;
}

.grades-points {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
}

.grade-point-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.grade-point-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 2px solid #cbd5e1;
  transition: all 0.2s;
}

.grade-point-wrapper.passed .grade-point-dot {
  border-color: #f25c45;
}

.grade-point-wrapper.active .grade-point-dot {
  background-color: #f25c45;
  border-color: #f25c45;
  transform: scale(1.2);
}

.grade-point-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  margin-top: 6px;
}

.grade-point-wrapper.active .grade-point-label {
  color: #f25c45;
}

.calc-divider {
  height: 1px;
  background-color: #eaecef;
  margin: 24px 0;
}

.periods-scroll-row-mini {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.period-rate-card-mini {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 42px;
  border: 1.5px solid #eaecef;
  border-radius: 10px;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
}

.period-rate-card-mini.active {
  border-color: #f25c45;
  background: #fff4f2;
}

.period-discount-badge-mini {
  position: absolute;
  top: -8px;
  right: 2px;
  padding: 1px 4.5px;
  font-weight: 850;
  font-size: 8px;
  color: #ffffff;
  border-radius: 6px;
  background: #22c55e;
}

.period-title-mini {
  font-weight: 750;
  font-size: 11px;
  color: #4b5563;
}

.period-rate-card-mini.active .period-title-mini {
  color: #f25c45;
}

.calc-totals-column {
  margin-top: 24px;
}

.promo-switch-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border: 1px solid #eaecef;
  border-radius: 16px;
  background-color: #ffffff;
  margin-bottom: 24px;
}

.promo-switch-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.promo-switch-title {
  font-weight: 750;
  font-size: 13px;
  color: #1f2937;
}

.promo-switch-desc {
  font-size: 11px;
  color: #6b7280;
}

.switch-control {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.switch-control input {
  width: 0;
  height: 0;
  opacity: 0;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 24px;
  background-color: #cbd5e1;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.switch-slider::before {
  content: "";
  position: absolute;
  left: 3px;
  bottom: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ffffff;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.switch-control input:checked + .switch-slider {
  background-color: #10b981;
}

.switch-control input:checked + .switch-slider::before {
  transform: translateX(20px);
}

.total-price-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.total-price-section {
  text-align: left;
}

.total-header {
  font-weight: 700;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 4px;
}

.price-display-wrapper {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.total-big-price {
  font-weight: 900;
  font-size: 36px;
  color: #f25c45;
  line-height: 1;
}

.total-monthly-hint {
  font-weight: 700;
  font-size: 14px;
  color: #6b7280;
}

.pricing-roi-callout {
  display: flex;
  align-items: center;
}

.roi-strong-text {
  font-weight: 850;
  font-size: 13px;
  color: #10b981;
}

.cta-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.trust-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
}

.check-circle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #ecfdf5;
  color: #10b981;
}

/* SECTION 9: COMPARISON */
.comparison-section {
  padding: 50px 0 60px;
  border-top: 1px solid #eaecef;
  background-color: #ffffff;
}

.table-scroll-container {
  width: 100%;
  overflow-x: auto;
  border-radius: 16px;
  border: 1px solid #eaecef;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
}

th {
  padding: 10px 12px;
  font-size: 12px;
  color: #6b7280;
  background-color: #f8f9fb;
}

td {
  padding: 10px 10px;
  border-top: 1px solid #eaecef;
}

@media (min-width: 992px) {
  th {
    padding: 14px 16px;
    font-size: 14px;
  }

  td {
    padding: 14px 16px;
  }
}

.table-param-header {
  min-width: 120px;
}

.table-donut-header {
  background-color: #f25c45;
  color: #ffffff;
  min-width: 110px;
}

.donut-header-content {
  display: flex;
  align-items: center;
}

.donut-title {
  font-weight: 850;
  font-size: 15px;
}

.table-competitor-header {
  background-color: #f8f9fb;
  min-width: 120px;
}

.comp-title {
  font-weight: 750;
  font-size: 13px;
}

.comp-sub {
  display: block;
  font-size: 10.5px;
  color: #9ca3af;
  margin-top: 2px;
}

.cell-param {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 12px;
  color: #1f2937;
}

.param-icon-wrapper {
  color: #f25c45;
  display: flex;
  align-items: center;
}

.param-icon-wrapper svg {
  width: 16px;
  height: 16px;
}

.cell-donut {
  background-color: #fff4f2;
  border-left: 2px solid rgb(242 92 69 / 8%);
  border-right: 2px solid rgb(242 92 69 / 8%);
}

.table-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 12px;
}

.table-badge-icon {
  display: flex;
  align-items: center;
}

/* SECTION 10: FINAL CTA */
.final-cta-section {
  position: relative;
  padding: 50px 0;
  overflow: hidden;
  border-top: 1px solid #eaecef;
  background-color: #ffffff;
}

.cta-bg-glow {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 500px;
  height: 300px;
  background: radial-gradient(ellipse at top, rgb(242 92 69 / 4%) 0%, transparent 65%);
  pointer-events: none;
}

.cta-grid-layout {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 32px;
}

.cta-info-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.cta-headline {
  margin: 0;
  font-weight: 850;
  font-size: 28px;
  color: #1f2937;
  line-height: 1.2;
}

.cta-subtitle {
  margin: 0;
  font-size: 15px;
  color: #6b7280;
  line-height: 1.5;
}

.cta-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.cta-micro-trust {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.cta-image-col {
  display: flex;
  justify-content: center;
}

.cta-mockup-img {
  width: 100%;
  max-width: 440px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* RESPONSIVENESS AND DESKTOP ADAPTATION */
@media (min-width: 480px) {
  .hero-cta-group,
  .cta-buttons {
    flex-direction: row;
    justify-content: center;
  }
  .hero-cta-group button,
  .hero-cta-group a,
  .cta-buttons button {
    flex: initial;
    min-width: 180px;
  }
}

@media (min-width: 769px) {
  .section-container {
    padding: 0 32px;
  }

  .section-title {
    font-size: 38px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  /* HERO */
  .hero-section {
    padding: 70px 0 50px;
  }

  .hero-grid {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 48px;
  }

  .hero-info {
    text-align: left;
  }

  .hero__badge-wrapper {
    justify-content: flex-start;
  }

  .hero-title {
    font-size: 48px;
    min-height: 110px;
  }

  .hero-subtitle {
    font-size: 17px;
    max-width: 480px;
  }

  .hero-cta-group {
    justify-content: flex-start;
  }

  .hero-visual {
    height: 550px;
  }

  .tg-notif-card {
    width: 310px;
    padding: 12px 16px;
    border-radius: 20px;
  }

  .tg-notif-order {
    top: 30px;
    left: -30px;
  }

  .tg-notif-reminder {
    right: -30px;
    bottom: 30px;
  }

  /* NAVIGATION CARDS */
  .nav-grid {
    flex-direction: row;
    gap: 32px;
  }

  .nav-card {
    flex: 1;
    min-height: 320px;
    padding: 48px 40px;
  }

  .nav-card-title {
    font-size: 26px;
  }

  /* WHY DONUT */
  .why-section {
    padding: 70px 0 90px;
  }

  .why-grid-layout {
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    align-items: center;
    gap: 48px;
    margin-top: 24px;
  }

  .why-tabs-row,
  .why-active-desc-container {
    display: none;
  }

  .why-slider-nav-desktop {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .why-nav-item {
    display: flex;
    flex-direction: column;
    padding: 16px 20px;
    border: 1px solid #eaecef;
    border-radius: 16px;
    background: #f8f9fb;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    cursor: pointer;
  }

  .why-nav-item:hover {
    border-color: #cbd5e1;
    background: #ffffff;
  }

  .why-nav-item.active {
    border-color: #f25c45;
    background: #fff4f2;
    box-shadow: 0 4px 18px rgb(242 92 69 / 6%);
  }

  .why-nav-header-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .why-nav-num {
    font-weight: 800;
    font-size: 12px;
    color: #9ca3af;
  }

  .why-nav-item.active .why-nav-num {
    color: #f25c45;
  }

  .why-nav-title {
    margin: 0;
    font-weight: 800;
    font-size: 15px;
    color: #4b5563;
  }

  .why-nav-item.active .why-nav-title {
    color: #1f2937;
  }

  .why-nav-body {
    margin-top: 8px;
    padding-left: 28px;
  }

  .why-nav-text {
    margin: 0;
    font-size: 13.5px;
    color: #6b7280;
    line-height: 1.5;
  }

  .why-screenshot-wrapper {
    max-width: 460px;
  }

  /* ADVANTAGES */
  .index__advantages {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 60px;
    padding: 60px 48px;
    border-radius: 50px;
  }

  /* VIEW */
  .view__picture {
    padding-top: 50px;
    row-gap: 0;
    margin-inline: 0;
  }

  .view__image {
    position: relative;
    left: 120px;
    translate: none;
    width: 870px;
    height: 570px;
  }

  .view__icon {
    display: block;
  }

  .view__item {
    position: absolute;
    width: 300px;
    padding: 20px;
  }

  .view__item--1 {
    top: 0;
    left: 60px;
  }
  .view__item--2 {
    top: 0;
    right: 60px;
  }
  .view__item--3 {
    top: 136px;
    left: 0;
  }
  .view__item--4 {
    top: 136px;
    right: 0;
  }
  .view__item--5 {
    top: 272px;
    left: 0;
  }
  .view__item--6 {
    top: 272px;
    right: 0;
  }
  .view__item--7 {
    top: 408px;
    left: 60px;
  }
  .view__item--8 {
    top: 408px;
    right: 60px;
  }

  .view__name {
    font-size: 16px;
  }

  /* CASES */
  .testimonials-section {
    padding: 70px 0;
  }
  .section-cases__slider {
    margin: 0;
    padding: 0;
  }
  .section-cases__slider :deep(.swiper-slide) {
    width: 300px;
  }

  /* WORK */
  .work__list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 20px;
    row-gap: 40px;
  }
  .work__item {
    max-width: none;
    height: 100%;
    align-items: center;
    flex-direction: column;
    padding: 24px;
  }

  /* PRICING */
  .pricing-section {
    padding: 70px 0;
  }

  .pricing-calculator {
    display: grid;
    grid-template-columns: 1.25fr 0.75fr;
    gap: 48px;
    padding: 36px;
  }

  .calc-totals-column {
    margin-top: 0;
    padding-left: 48px;
    border-left: 1px solid #eaecef;
  }

  .calc-parameter-header {
    flex-direction: row;
    gap: 16px;
  }

  .calc-parameter-row {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  /* SECTION 5: COMPARISON */
  .comparison-section {
    padding: 50px 0 60px;
    border-top: 1px solid #eaecef;
    background-color: #ffffff;
  }

  .table-scroll-container {
    width: 100%;
    margin-bottom: 20px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .comparison-table {
    width: 100%;
    text-align: left;
    overflow: hidden;
    border-collapse: collapse;
    border: 1px solid #eaecef;
    border-radius: 16px;
  }

  th {
    padding: 10px 12px;
    font-size: 12px;
    color: #6b7280;
    background-color: #f8f9fb;
  }

  td {
    padding: 10px 12px;
    border-top: 1px solid #eaecef;
  }

  .table-param-header {
    min-width: 120px;
  }

  .table-donut-header {
    position: relative;
    min-width: 130px;
    padding: 14px 16px;
    color: #ffffff;
    background-color: #f25c45;
  }

  .donut-header-content {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .donut-header-svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  .donut-title {
    font-weight: 850;
    font-size: 14px;
  }

  .recommend-badge {
    display: inline-block;
    margin-top: 4px;
    padding: 1.5px 6px;
    font-weight: 700;
    font-size: 8px;
    color: #ffffff;
    border-radius: 8px;
    background: rgb(255 255 255 / 18%);
  }

  .table-competitor-header {
    min-width: 120px;
    background-color: #f8f9fb;
  }

  .comp-title {
    font-weight: 700;
    font-size: 12.5px;
    color: #6b7280;
  }

  .comp-sub {
    display: block;
    margin-top: 1px;
    font-size: 10.5px;
    color: #9ca3af;
  }

  .cell-param {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 12.5px;
  }

  .param-icon-wrapper {
    display: flex;
    align-items: center;
    color: #f25c45;
  }

  .param-icon-wrapper svg {
    width: 14px;
    height: 14px;
  }

  .cell-donut {
    border-right: 2px solid rgb(242 92 69 / 8%);
    border-left: 2px solid rgb(242 92 69 / 8%);
    background-color: #fff4f2;
  }

  .badge-status-container {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .table-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    width: fit-content;
    font-weight: 500;
    font-size: 12px;
    border-radius: 7px;
  }

  .table-badge-icon {
    display: flex;
    align-items: center;
  }

  .badge-sub {
    padding-left: 4px;
    font-size: 9.5px;
    color: #6b7280;
  }

  .comparison-footer {
    margin-top: 20px;
  }

  .footer-cta-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
  }

  .footer-migration-note {
    font-size: 11.5px;
    color: #6b7280;
  }

  .comparison-footnote {
    margin: 0;
    font-size: 10.5px;
    color: #6b7280;
  }
  /* FINAL CTA */
  .final-cta-section {
    padding: 80px 0;
  }

  .cta-grid-layout {
    grid-template-columns: 1fr 1.1fr;
    gap: 48px;
  }

  .cta-info-col {
    align-items: flex-start;
    text-align: left;
  }

  .cta-headline {
    font-size: 38px;
  }

  .cta-buttons {
    flex-direction: row;
    width: fit-content;
  }

  .cta-buttons button {
    min-width: 180px;
  }

  .cta-micro-trust {
    justify-content: flex-start;
  }

  /* TESTIMONIALS SLIDER */
  .testimonials-combined-section {
    padding: 70px 0 90px;
  }
  .testimonials__slider {
    margin: 0;
    padding: 0;
  }
  .testimonials__slider :deep(.swiper-slide) {
    width: 330px;
  }
}

.hover-lift {
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.hover-lift:hover {
  transform: translateY(-3px);
}

.comparison-footnote {
  margin: 0;
  font-size: 10.5px;
  color: #6b7280;
}

.comparison-footer {
  margin-top: 20px;
}
</style>
